import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type {
  Condition,
  Filter,
  FindManyOptions,
  OrGroup,
  Page,
  Repository,
  UpdatePatch,
} from "@/domain/repositoryInterface/Repository";

type PublicSchema = Database["public"];
type TableName = keyof PublicSchema["Tables"];
type RowOf<T extends TableName> = PublicSchema["Tables"][T]["Row"];
type InsertOf<T extends TableName> = PublicSchema["Tables"][T]["Insert"];
type UpdateOf<T extends TableName> = PublicSchema["Tables"][T]["Update"];

function isOrGroup(c: Condition): c is OrGroup {
  return Object.prototype.hasOwnProperty.call(c, "or");
}

/** OR グループ内の 1 条件を PostgREST の or() トークン（"col.op.val"）へ変換 */
function orToken(f: Filter): string {
  switch (f.op) {
    case "isNull":
      return `${f.column}.is.null`;
    case "notNull":
      return `${f.column}.not.is.null`;
    case "in":
      return `${f.column}.in.(${(f.value as unknown[]).join(",")})`;
    case "notIn":
      return `${f.column}.not.in.(${(f.value as unknown[]).join(",")})`;
    default:
      return `${f.column}.${f.op}.${f.value}`;
  }
}

/**
 * supabase SDK を閉じ込めた汎用リポジトリ基底。
 *
 * テーブル名のジェネリック T に対し型付き Row/Insert/Update を外部へ公開する。
 * クエリビルダの内部では SDK の厳格なジェネリック衝突を避けるため any 境界を用いるが、
 * 公開シグネチャは Repository<Row, Insert, Update> で型安全。
 */
export class SupabaseRepository<T extends TableName>
  implements Repository<RowOf<T>, InsertOf<T>, UpdateOf<T>>
{
  constructor(
    protected readonly table: T,
    protected readonly client = supabase,
  ) {}

  /** 条件群（AND）をクエリへ適用 */
  protected applyConditions<Q>(query: Q, where?: Condition[]): Q {
    if (!where?.length) return query;
    // deno-lint / supabase の filter builder は any 扱い
    let q = query as any;
    for (const c of where) {
      if (isOrGroup(c)) {
        q = q.or(c.or.map(orToken).join(","));
        continue;
      }
      const f = c as Filter;
      switch (f.op) {
        case "eq":
          q = q.eq(f.column, f.value);
          break;
        case "neq":
          q = q.neq(f.column, f.value);
          break;
        case "in":
          q = q.in(f.column, f.value as unknown[]);
          break;
        case "notIn":
          q = q.not(f.column, "in", `(${(f.value as unknown[]).join(",")})`);
          break;
        case "isNull":
          q = q.is(f.column, null);
          break;
        case "notNull":
          q = q.not(f.column, "is", null);
          break;
        case "gt":
          q = q.gt(f.column, f.value);
          break;
        case "gte":
          q = q.gte(f.column, f.value);
          break;
        case "lt":
          q = q.lt(f.column, f.value);
          break;
        case "lte":
          q = q.lte(f.column, f.value);
          break;
        case "ilike":
          q = q.ilike(f.column, f.value as string);
          break;
      }
    }
    return q as Q;
  }

  async findById<R = RowOf<T>>(id: string, opts?: { select?: string }): Promise<R | null> {
    const { data, error } = await (this.client.from(this.table) as any)
      .select(opts?.select ?? "*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return (data as R) ?? null;
  }

  async findByIds<R = RowOf<T>>(ids: string[], opts?: { select?: string }): Promise<R[]> {
    if (ids.length === 0) return [];
    const { data, error } = await (this.client.from(this.table) as any)
      .select(opts?.select ?? "*")
      .in("id", ids);
    if (error) throw error;
    return (data as R[]) ?? [];
  }

  async findMany<R = RowOf<T>>(opts: FindManyOptions = {}): Promise<Page<R>> {
    let query = (this.client.from(this.table) as any).select(
      opts.select ?? "*",
      opts.withCount ? { count: "exact" } : undefined,
    );
    query = this.applyConditions(query, opts.where);
    for (const o of opts.orderBy ?? []) {
      query = query.order(o.column, { ascending: o.ascending ?? true });
    }
    if (opts.page) {
      const from = opts.page.index * opts.page.size;
      query = query.range(from, from + opts.page.size - 1);
    }
    const { data, error, count } = await query;
    if (error) throw error;
    return { data: (data as R[]) ?? [], count: count ?? null };
  }

  async create(input: InsertOf<T> | InsertOf<T>[]): Promise<RowOf<T>[]> {
    const rows = Array.isArray(input) ? input : [input];
    const { data, error } = await (this.client.from(this.table) as any)
      .insert(rows)
      .select();
    if (error) throw error;
    return (data as RowOf<T>[]) ?? [];
  }

  async update(
    patches: UpdatePatch<UpdateOf<T>> | UpdatePatch<UpdateOf<T>>[],
  ): Promise<RowOf<T>[]> {
    const list = Array.isArray(patches) ? patches : [patches];
    const results: RowOf<T>[] = [];
    for (const { id, patch } of list) {
      const { data, error } = await (this.client.from(this.table) as any)
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      results.push(data as RowOf<T>);
    }
    return results;
  }

  async delete(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await (this.client.from(this.table) as any).delete().in("id", ids);
    if (error) throw error;
  }
}
